import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useIntl } from 'react-intl-next';
import uuid from 'uuid/v4';

import ErrorIcon from '@atlaskit/icon/core/migration/error';
import type { ADNode } from '@atlaskit/editor-common/validator';
import type { EditorActions } from '@atlaskit/editor-core';
import { FabricChannel } from '@atlaskit/analytics-listeners';
import {
	useAnalyticsEvents,
	AnalyticsListener,
	type UIAnalyticsEvent,
} from '@atlaskit/analytics-next';
import MentionIcon from '@atlaskit/icon/core/mention';
import Avatar from '@atlaskit/avatar/Avatar';

import { ErrorDisplay } from '@confluence/error-boundary';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data';
import { useShareToEntities } from '@confluence/share';
import type { FlagsStateContainer } from '@confluence/flags';

import { NotificationAction } from './graphql/__types__/CreateContentMentionNotificationAction';
import { useContentPermissions } from './hooks/useContentPermissions';
import { useUsersContentPermissions } from './hooks/useUsersContentPermissions';
import { useMentionsUserInfoQuery } from './hooks/useMentionsUserInfoQuery';
import { useUsersInfoQuery } from './hooks/useUsersInfoQuery';
import { useCreateMentionNotificationMutation } from './hooks/useCreateMentionNotificationMutation';
import type { MentionNotificationType } from './hooks/useMentionNotificationMutation';
import { useMentionNotificationMutation } from './hooks/useMentionNotificationMutation';
import type { UpdatedTaskItem } from './hooks/useCreateInlineTaskNotificationMutation';
import { useCreateInlineTaskNotificationMutation } from './hooks/useCreateInlineTaskNotificationMutation';
import { ContentRole, PrincipalType } from './graphql/__types__/UpdateContentPermissionsMutation';
import { Operation } from './graphql/__types__/CreateInlineTaskNotificationMutation';
import { mentionsMessages } from './mentionsi18n';
import type { MentionType } from './hooks/useMentionsNotifications';
import { useMentionsNotifications } from './hooks/useMentionsNotifications';
import { getElapsedTime } from './mentionsUtils';

type MentionsNotificationsWrapperProps = {
	children: React.ReactNode;
	flags: FlagsStateContainer;
	contentId: string;
	isMentionsNotificationsEnabled: boolean;
	editorActions?: EditorActions;
};

type ShowCustomFlagProps = {
	title: string;
	description: string;
	userPhoto?: string;
	autoDismiss?: boolean;
	id?: string;
};

type ShowCustomFlagWithActionProps = {
	title: string;
	description: string;
	userPhoto?: string;
	actionContent: string;
	actionOnClick: () => void;
	handleOnClose?: () => void;
	autoDismiss?: boolean;
	id?: string;
};

type ShowFlagForUserProps = {
	displayName: string;
	userPhoto?: string;
};

const getDisplayNames = (mentions: MentionType[]) => {
	const displayNames = new Set(mentions.map((mention) => mention.displayName));
	const iterator = displayNames.values();
	const displayName = iterator.next().value ?? '';
	const somebodyElse = iterator.next().value ?? '';
	return { displayNames, displayName, somebodyElse };
};

export const MentionsNotificationsWrapper = ({
	children,
	flags,
	contentId,
	isMentionsNotificationsEnabled,
	editorActions,
}: MentionsNotificationsWrapperProps) => {
	const [
		{
			addedMentions,
			deletedMentions,
			addedTasks,
			unrestrictedMentions,
			parentSpaceRestrictedMentions,
			pageRestrictedMentions,
			unrestrictedTasks,
			pageRestrictedTasks,
			parentSpaceRestrictedTasks,
			deletedTasks,
			mentionsAdded,
			tasksAdded,
		},
		{
			setAddedMentions,
			setDeletedMentions,
			setUnrestrictedMentions,
			setParentSpaceRestrictedMentions,
			setPageRestrictedMentions,
			setUnrestrictedTasks,
			setPageRestrictedTasks,
			setParentSpaceRestrictedTasks,
			setDeletedTasks,
			setMentionsAdded,
			setTasksAdded,
		},
	] = useMentionsNotifications();

	const [mentionedUserId, setMentionedUserId] = useState<string>('');
	const [mentionLocalId, setMentionLocalId] = useState<string>('');
	const [taskItem, setTaskItem] = useState({
		operation: '',
		recipientAccountId: '',
		taskId: '',
	});
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { userId: currentUserId } = useSessionData();
	const { shareToEntities } = useShareToEntities(contentId || '');
	//General Mention Flag IDs
	const [currentAddedMentionFlagId, setCurrentAddedMentionFlagId] = useState<string>('');
	const [
		currentSpaceOrParentRestrictionMentionFlagId,
		setCurrentSpaceOrParentRestrictionMentionFlagId,
	] = useState('');
	const [currentPageRestrictionMentionFlagId, setCurrentPageRestrictionMentionFlagId] =
		useState('');
	//Task Mentions Flag IDs
	const [currentAddedTaskFlagId, setCurrentAddedTaskFlagId] = useState('');
	const [currentPageRestrictionTaskFlagId, setCurrentPageRestrictionTaskFlagId] = useState('');
	const [currentSpaceOrParentRestrictionTaskFlagId, setCurrentSpaceOrParentRestrictionTaskFlagId] =
		useState('');
	//General Mention Timeouts
	const addedUnrestrictedMentionFlagTimeout = useRef<ReturnType<typeof setTimeout> | undefined>();
	const pageRestrictedMentionFlagTimeout = useRef<ReturnType<typeof setTimeout> | undefined>();
	const spaceParentRestrictedMentionFlagTimeout = useRef<
		ReturnType<typeof setTimeout> | undefined
	>();
	//Task Mention Timeouts
	const addedUnrestrictedTaskFlagTimeout = useRef<ReturnType<typeof setTimeout> | undefined>();
	const pageRestrictedTaskFlagTimeout = useRef<ReturnType<typeof setTimeout> | undefined>();
	const spaceParentRestrictedTaskFlagTimeout = useRef<ReturnType<typeof setTimeout> | undefined>();

	const [error, setError] = useState<Error | null>(null);

	// Inline comment sidebar uses the same editor component as the one that powers the page itself
	const isSidebarPresent =
		document.querySelectorAll('.ak-editor-content-area > .ProseMirror').length > 1;

	const resetState = () => {
		setMentionedUserId('');
		setMentionLocalId('');
		setTaskItem({
			operation: '',
			recipientAccountId: '',
			taskId: '',
		});
	};

	// Hooks for fetching user display name, content permissions, and sending notifications
	const { userInfoData, userInfoError } = useMentionsUserInfoQuery({
		mentionedUserId,
		currentUserId,
		isMentionsNotificationsEnabled,
	});

	const deletedUserIds = [
		...deletedMentions?.map((mention) => mention.id),
		...deletedTasks?.map((mention) => mention.id),
	];
	const {
		usersInfoData: deletedMentionedUsersInfoData,
		usersInfoError: deletedMentionedUsersInfoError,
	} = useUsersInfoQuery({
		userIds: deletedUserIds,
		isUpdatedMentionsInLivePagesEnabled:
			isMentionsNotificationsEnabled && fg('confluence_updated_mentions_livepages'),
	});

	const showErrorFlag = useCallback(
		(displayName: string) => {
			void flags.showErrorFlag({
				title: formatMessage(mentionsMessages.mentionFlagFailedTitle, {
					displayName,
				}),
				description: formatMessage(mentionsMessages.mentionFlagFailedDescription),
				isAutoDismiss: true,
			});
		},
		[flags, formatMessage],
	);

	const {
		userHasAccessToContent,
		isParentPageRestricted,
		isSpaceRestricted,
		contentPermissionsError,
		contentPermissionsLoading,
		updateContentPermissionMutation,
	} = useContentPermissions({
		contentId,
		mentionedUserId,
		currentUserId,
		isMentionsNotificationsEnabled,
	});

	const mentionsToDisplay = useMemo(
		() => [...addedMentions, ...addedTasks],
		[addedMentions, addedTasks],
	);

	const {
		usersContentPermissionsError,
		usersContentPermissionsLoading,
		updateUsersContentPermissionMutation,
	} = useUsersContentPermissions({
		contentId,
		isUpdatedMentionsInLivePagesEnabled:
			isMentionsNotificationsEnabled && fg('confluence_updated_mentions_livepages'),
		currentUserId,
		mentionsToDisplay,
	});

	useEffect(() => {
		const getDuplicateMentions = async (mentions: MentionType[]) => {
			const duplicateMentions = new Set();
			if (mentions.length > 0) {
				const mentionCounts = new Map();
				const editorContent: ADNode = await editorActions?.getValue();
				editorContent?.content?.forEach((node) => {
					node?.content?.forEach((content) => {
						if (content.type === 'mention') {
							if (mentionCounts.has(content.attrs.id)) {
								mentionCounts.set(content.attrs.id, mentionCounts.get(content.attrs.id) + 1);
							} else {
								mentionCounts.set(content.attrs.id, 1);
							}
						}
					});
				});

				mentions.forEach((mention) => {
					if (mentionCounts.get(mention.id) > 1) {
						duplicateMentions.add(mention.id);
					}
				});
			}
			return duplicateMentions;
		};

		getDuplicateMentions(mentionsToDisplay)
			.then((duplicateMentions) => {
				if (duplicateMentions.size > 0) {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							source: 'mentionsNotifications',
							action: 'created',
							actionSubject: 'mention',
							attributes: {
								isDuplicate: true,
							},
						},
					}).fire();
				}
			})
			.catch((e) => {
				setError(e);
			});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mentionsToDisplay]);

	const { sendCreateMentionNotification } = useCreateMentionNotificationMutation({
		contentId,
		showErrorFlag,
	});

	const mentionsFlagStartTime = useRef<number | null>(null);
	const tasksFlagStartTime = useRef<number | null>(null);

	// `showCustomFlag` for confirmation flags without CTA
	const showCustomFlag = useCallback(
		({ title, description, userPhoto, autoDismiss = true, id }: ShowCustomFlagProps) => {
			void flags.showCustomFlag({
				id: id ?? undefined,
				title,
				description,
				isAutoDismiss: autoDismiss,
				customIcon: userPhoto ? (
					<Avatar src={userPhoto} label="mention" />
				) : (
					<MentionIcon spacing="spacious" label="mention" />
				),
			});
		},
		[flags],
	);

	const showDontNotifyFailedFlag = useCallback(() => {
		void flags.showCustomFlag({
			title: formatMessage(mentionsMessages.dontNotifyFailureTitle),
			description: formatMessage(mentionsMessages.dontNotifyFailureDescription),
			customIcon: <ErrorIcon spacing="spacious" label="error" />,
			isAutoDismiss: true,
		});
	}, [flags, formatMessage]);

	const {
		sendMentionNotification,
		sendCancelMentionNotification,
		contentMentionNotificationError,
	} = useMentionNotificationMutation({
		contentId,
		setError,
		showDontNotifyFailedFlag,
	});

	const {
		sendCreateInlineTaskNotification,
		sendCreateInlineTaskNotifications,
		cancelInlineTaskNotifications,
		createTaskNotificationError,
	} = useCreateInlineTaskNotificationMutation({
		contentId,
		showErrorFlag,
		setError,
		showDontNotifyFailedFlag,
	});

	// `showCustomFlagWithAction` for:
	// - unrestricted users with "do not notify" CTA
	// - share now for restricted users
	// - deleted mention flag
	const showCustomFlagWithAction = useCallback(
		({
			title,
			description,
			userPhoto,
			actionContent,
			actionOnClick,
			handleOnClose,
			autoDismiss = true,
			id,
		}: ShowCustomFlagWithActionProps) => {
			void flags.showCustomFlag({
				title,
				description,
				isAutoDismiss: autoDismiss,
				customIcon: userPhoto ? (
					<Avatar src={userPhoto} label="mention" />
				) : (
					<MentionIcon spacing="spacious" label="mention" />
				),
				onClose: handleOnClose && handleOnClose,
				actions: [
					{
						content: actionContent,
						onClick: actionOnClick,
					},
				],
				id: id ?? undefined,
			});
		},
		[flags],
	);

	const showFlagForRestrictedUser = useCallback(
		({ displayName, userPhoto }: ShowFlagForUserProps) => {
			const isTaskMention = taskItem.recipientAccountId;

			// Page can have restrictions or restrictions inherited from parent
			if (isSpaceRestricted) {
				showCustomFlag({
					title: isTaskMention
						? formatMessage(mentionsMessages.taskMentionFlagNoPermissionsTitle, {
								displayName,
							})
						: formatMessage(mentionsMessages.mentionFlagNoPermissionsTitle, {
								displayName,
							}),
					description: isTaskMention
						? formatMessage(
								mentionsMessages.taskMentionFlagNoPermissionsSpaceRestrictionsDescription,
							)
						: formatMessage(mentionsMessages.mentionFlagNoPermissionsSpaceRestrictionsDescription),
					userPhoto,
				});
			} else if (isParentPageRestricted) {
				showCustomFlag({
					title: isTaskMention
						? formatMessage(mentionsMessages.taskMentionFlagNoPermissionsTitle, {
								displayName,
							})
						: formatMessage(mentionsMessages.mentionFlagNoPermissionsTitle, {
								displayName,
							}),
					description: isTaskMention
						? formatMessage(
								mentionsMessages.taskMentionFlagNoPermissionsParentRestrictionsDescription,
							)
						: formatMessage(mentionsMessages.mentionFlagNoPermissionsParentRestrictionsDescription),
					userPhoto,
				});
			} else {
				// Mentioned user has no access to page
				showCustomFlagWithAction({
					title: isTaskMention
						? formatMessage(mentionsMessages.taskMentionFlagNoPermissionsTitle, {
								displayName,
							})
						: formatMessage(mentionsMessages.mentionFlagNoPermissionsTitle, {
								displayName,
							}),
					description: isTaskMention
						? formatMessage(mentionsMessages.taskMentionFlagNoPermissionsDescription)
						: formatMessage(mentionsMessages.mentionFlagNoPermissionsDescription),
					userPhoto,
					actionContent: formatMessage(mentionsMessages.mentionFlagShareNowButton),
					actionOnClick: async () => {
						resetState();

						// Updates page restrictions
						void updateContentPermissionMutation({
							variables: {
								contentId,
								input: {
									contentRole: ContentRole.EDITOR,
									principalId: mentionedUserId,
									principalType: PrincipalType.USER,
								},
							},
						});

						const flagToDismiss = flags.state.flags[0]?.id;
						void flags.hideFlag(flagToDismiss);

						try {
							// Sends email notifications
							const response = await shareToEntities([
								{
									id: mentionedUserId,
									type: 'user',
								},
							]);

							if (response.success) {
								createAnalyticsEvent({
									type: 'sendTrackEvent',
									data: {
										source: 'mentionsNotifications',
										action: 'sent',
										actionSubject: 'shareNotification',
									},
								}).fire();
							}
						} catch (error) {
							createAnalyticsEvent({
								type: 'sendTrackEvent',
								data: {
									source: 'mentionsNotifications',
									action: 'failed',
									actionSubject: 'shareNotification',
								},
							}).fire();

							showCustomFlag({
								title: formatMessage(mentionsMessages.mentionFlagShareFailedTitle),
								description: formatMessage(mentionsMessages.mentionFlagShareFailedDescription),
							});
						}
					},
				});
			}
		},
		// `shareToEntities` is a hook responsible for sending email notifications to the mentioned user.
		// When included in the dependency array, for some reason, the flag infinitely re-renders.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			contentId,
			createAnalyticsEvent,
			flags,
			formatMessage,
			isParentPageRestricted,
			isSpaceRestricted,
			mentionedUserId,
			showCustomFlag,
			showCustomFlagWithAction,
			updateContentPermissionMutation,
		],
	);

	const showFlagForUnrestrictedUser = useCallback(
		({ displayName, userPhoto }: ShowFlagForUserProps) => {
			const isTaskMention = taskItem.recipientAccountId;

			showCustomFlagWithAction({
				title: isTaskMention
					? formatMessage(mentionsMessages.taskMentionFlagTitle, { displayName })
					: formatMessage(mentionsMessages.mentionFlagTitle, { displayName }),
				description: isTaskMention
					? formatMessage(mentionsMessages.taskMentionFlagDescription)
					: formatMessage(mentionsMessages.mentionFlagDescription),
				userPhoto,
				actionContent: formatMessage(mentionsMessages.mentionFlagDoNotNotifyButton),
				handleOnClose: () => {
					isTaskMention
						? sendCreateInlineTaskNotification({
								taskItem,
								displayName,
							})
						: sendCreateMentionNotification({
								mentionedUserId,
								displayName,
								mentionLocalId,
							});

					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							source: 'mentionsNotifications',
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: 'dismissMentionsNotifications',
							attributes: isTaskMention
								? {
										contentId,
										operation: taskItem.operation,
										mentionedUserId: taskItem.recipientAccountId,
										taskId: taskItem.taskId,
									}
								: {
										contentId,
										mentionedUserId,
										mentionLocalId,
									},
						},
					}).fire();

					resetState();

					void setAddedMentions([]);
				},
				actionOnClick: () => {
					resetState();

					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							source: 'mentionsNotifications',
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: isTaskMention ? 'doNotAssign' : 'doNotNotify',
							attributes: isTaskMention
								? {
										contentId,
										operation: taskItem.operation,
										mentionedUserId: taskItem.recipientAccountId,
										taskId: taskItem.taskId,
									}
								: {
										contentId,
										mentionedUserId,
										mentionLocalId,
									},
						},
					}).fire();

					// Only one flag shows at a given time, so we can just always target the "top" flag
					const flagToDismiss = flags.state.flags[0]?.id;
					void flags.hideFlag(flagToDismiss);
				},
			});
		},
		[
			contentId,
			createAnalyticsEvent,
			flags,
			formatMessage,
			mentionedUserId,
			showCustomFlagWithAction,
			sendCreateMentionNotification,
			sendCreateInlineTaskNotification,
			taskItem,
			mentionLocalId,
			setAddedMentions,
		],
	);

	const displayNoPermissionsFlag = useCallback(
		({
			displayNames,
			flagFields,
			displayName,
			somebodyElse,
			flagId,
			type = 'standard',
			hasAction = false,
			userPhoto,
		}) => {
			const showFlagFn = hasAction ? showCustomFlagWithAction : showCustomFlag;
			if (displayNames.size === 1) {
				showFlagFn({
					...flagFields,
					title:
						type === 'standard'
							? formatMessage(mentionsMessages.mentionFlagNoPermissionsTitle, {
									displayName,
								})
							: formatMessage(mentionsMessages.taskMentionFlagNoPermissionsTitle, {
									displayName,
								}),
					id: flagId,
					userPhoto,
				});
			} else if (displayNames.size === 2) {
				showFlagFn({
					...flagFields,
					title:
						type === 'standard'
							? formatMessage(mentionsMessages.mentionFlagTwoNoPermissionsTitle, {
									displayName,
									somebodyElse,
								})
							: formatMessage(mentionsMessages.taskMentionFlagTwoUsersNoPermissionsTitle, {
									displayName,
									somebodyElse,
								}),
					id: flagId,
				});
			} else {
				//3 or more mentioned users
				showFlagFn({
					...flagFields,
					title:
						type === 'standard'
							? formatMessage(mentionsMessages.mentionFlagMultipleNoPermissionsTitle, {
									displayName,
									somebodyElse,
									numOthers: displayNames.size - 2,
								})
							: formatMessage(mentionsMessages.taskMentionFlagMultipleUsersNoPermissionsTitle, {
									displayName,
									somebodyElse,
									numOthers: displayNames.size - 2,
								}),
					id: flagId,
				});
			}
		},
		[formatMessage, showCustomFlag, showCustomFlagWithAction],
	);

	const getNotificationPayload = (
		notifyAction: NotificationAction,
		mentions: MentionType[],
		type: 'standard' | 'task',
	) => {
		const mentionsWithNotificationAction: MentionNotificationType[] = [];
		const tasksWithNotificationAction: UpdatedTaskItem[] = [];
		mentions.forEach((mention) => {
			if (type === 'standard') {
				mentionsWithNotificationAction.push({
					mentionedUserAccountId: mention.id,
					mentionLocalId: mention.localId,
					notificationAction: notifyAction,
				});
			} else if (type === 'task') {
				tasksWithNotificationAction.push({
					notificationAction: notifyAction,
					operation: Operation.ASSIGNED,
					recipientAccountId: mention.id,
					recipientMentionLocalId: mention.localId,
					taskId: mention.taskLocalId ?? '',
				});
			}
		});
		return { mentionsWithNotificationAction, tasksWithNotificationAction };
	};

	const sendNotificationFn = (
		mentions: MentionType[],
		type: 'task' | 'standard',
		closeFlagFn: () => void,
	) => {
		const { mentionsWithNotificationAction, tasksWithNotificationAction } = getNotificationPayload(
			NotificationAction.NOTIFY,
			mentions,
			type,
		);
		if (type === 'standard') {
			sendMentionNotification({ mentions: mentionsWithNotificationAction });
		} else if (type === 'task') {
			sendCreateInlineTaskNotifications({ taskItems: tasksWithNotificationAction });
		}
		closeFlagFn();
	};

	const showUnrestrictedPageAccessFlag = useCallback(
		({
			type,
			mentions,
			userPhoto,
		}: {
			type: 'standard' | 'task';
			mentions: MentionType[];
			userPhoto?: string;
		}) => {
			const analyticsPayload = mentions.map((mention) => {
				if (type === 'standard') {
					return {
						mentionedUserId: mention.id,
						mentionId: mention.localId,
					};
				} else if (type === 'task') {
					return {
						taskId: mention.taskLocalId,
						mentionId: mention.localId,
						mentionedUserId: mention.id,
					};
				}
			});

			let addedMentionFlagId = uuid();
			const handleFlag = (
				currentFlagId: string,
				setCurrentFlagId: React.Dispatch<React.SetStateAction<string>>,
				flagStartTime: React.MutableRefObject<number | null>,
			) => {
				if (currentFlagId) {
					addedMentionFlagId = currentFlagId;
				} else {
					setCurrentFlagId(addedMentionFlagId);
					flagStartTime.current = Date.now();
				}
			};
			if (type === 'standard') {
				handleFlag(currentAddedMentionFlagId, setCurrentAddedMentionFlagId, mentionsFlagStartTime);
			} else if (type === 'task') {
				handleFlag(currentAddedTaskFlagId, setCurrentAddedTaskFlagId, tasksFlagStartTime);
			}
			const { displayNames, displayName, somebodyElse } = getDisplayNames(mentions);

			const closeFlag = () => {
				void flags.hideFlag(addedMentionFlagId);
				if (type === 'standard') {
					setCurrentAddedMentionFlagId('');
					void setUnrestrictedMentions([]);
					mentionsFlagStartTime.current = null;
				} else if (type === 'task') {
					setCurrentAddedTaskFlagId('');
					void setUnrestrictedTasks([]);
					tasksFlagStartTime.current = null;
				}
			};

			const sendDontNotifyFn = () => {
				const { mentionsWithNotificationAction, tasksWithNotificationAction } =
					getNotificationPayload(NotificationAction.DONT_NOTIFY, mentions, type);
				if (type === 'standard') {
					void sendCancelMentionNotification({
						mentions: mentionsWithNotificationAction,
					});
					clearTimeout(addedUnrestrictedMentionFlagTimeout.current);
				} else if (type === 'task') {
					cancelInlineTaskNotifications({
						taskItems: tasksWithNotificationAction,
					});
					clearTimeout(addedUnrestrictedTaskFlagTimeout.current);
				}
			};

			//When there is a flag currently open when a new mention is added, we want to reset the timer for the flag to be open, i.e. persist the flag to be open for another 8 seconds
			if (type === 'standard') {
				clearTimeout(addedUnrestrictedMentionFlagTimeout.current);
				addedUnrestrictedMentionFlagTimeout.current = setTimeout(() => {
					sendNotificationFn(mentions, type, closeFlag);
				}, 8000);
			} else if (type === 'task') {
				clearTimeout(addedUnrestrictedTaskFlagTimeout.current);
				addedUnrestrictedTaskFlagTimeout.current = setTimeout(() => {
					sendNotificationFn(mentions, type, closeFlag);
				}, 8000);
			}

			const sharedFlagFields = {
				description:
					type === 'standard'
						? formatMessage(mentionsMessages.mentionFlagDescription)
						: formatMessage(mentionsMessages.taskMentionFlagDescription),
				actionContent: formatMessage(mentionsMessages.mentionFlagDoNotNotifyButton),
				actionOnClick: () => {
					//On clicking "don't notify"
					const elapsedTimeInSeconds =
						type === 'standard'
							? getElapsedTime(mentionsFlagStartTime)
							: getElapsedTime(tasksFlagStartTime);

					sendDontNotifyFn();
					closeFlag();
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							source: 'mentionsNotifications',
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: type === 'standard' ? 'doNotNotify' : 'doNotAssign',
							attributes: {
								contentId,
								mentions: analyticsPayload,
								elapsedTimeInSeconds,
							},
						},
					}).fire();
				},
				handleOnClose: () => {
					//when "X" is clicked
					const elapsedTimeInSeconds =
						type === 'standard'
							? getElapsedTime(mentionsFlagStartTime)
							: getElapsedTime(tasksFlagStartTime);

					closeFlag();
					sendDontNotifyFn();
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							source: 'mentionsNotifications',
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId:
								type === 'standard'
									? 'dismissMentionsNotifications'
									: 'dismissTaskMentionsNotifications',
							attributes: {
								contentId,
								elapsedTimeInSeconds,
								mentions: mentions.map((mention) => ({
									mentionedUserId: mention.id,
									mentionId: mention.localId,
								})),
							},
						},
					}).fire();
				},
				autoDismiss: false,
			};

			if (displayNames.size === 1) {
				showCustomFlagWithAction({
					...sharedFlagFields,
					title: formatMessage(mentionsMessages.mentionFlagTitle, {
						displayName,
					}),
					id: addedMentionFlagId,
					userPhoto,
				});
			} else if (displayNames.size === 2) {
				showCustomFlagWithAction({
					...sharedFlagFields,
					title: formatMessage(mentionsMessages.mentionFlagTitleTwoMentions, {
						displayName,
						somebodyElse,
					}),
					id: addedMentionFlagId,
				});
			} else {
				//3 or more mentioned users
				showCustomFlagWithAction({
					...sharedFlagFields,
					title: formatMessage(mentionsMessages.mentionFlagTitleMultipleMentions, {
						displayName,
						somebodyElse,
						numOthers: displayNames.size - 2,
					}),
					id: addedMentionFlagId,
				});
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			contentId,
			createAnalyticsEvent,
			currentAddedMentionFlagId,
			flags,
			formatMessage,
			sendCancelMentionNotification,
			sendMentionNotification,
			setUnrestrictedMentions,
			showCustomFlagWithAction,
			currentAddedTaskFlagId,
			sendCreateInlineTaskNotifications,
			setUnrestrictedTasks,
			cancelInlineTaskNotifications,
			mentionsFlagStartTime,
			tasksFlagStartTime,
		],
	);

	const showPageRestrictionsFlag = useCallback(
		({ type, mentions }: { type: 'standard' | 'task'; mentions: MentionType[] }) => {
			let addedMentionFlagId = uuid();
			//If a flag ID is already in local state, that means the respective flag is currently open. If not, we generate a uuid to set on the flag which is about to be opened. Having a uuid on the flag allows us to update the content without closing the flag
			if (type === 'standard' && currentPageRestrictionMentionFlagId) {
				addedMentionFlagId = currentPageRestrictionMentionFlagId;
			} else if (type === 'task' && currentPageRestrictionTaskFlagId) {
				addedMentionFlagId = currentPageRestrictionTaskFlagId;
			}
			if (type === 'standard' && !currentPageRestrictionMentionFlagId) {
				setCurrentPageRestrictionMentionFlagId(addedMentionFlagId);
			}
			if (type === 'task' && !currentPageRestrictionTaskFlagId) {
				setCurrentPageRestrictionTaskFlagId(addedMentionFlagId);
			}

			const { displayNames, displayName, somebodyElse } = getDisplayNames(mentions);

			const closeFlag = () => {
				void flags.hideFlag(addedMentionFlagId);
				if (type === 'standard') {
					clearTimeout(pageRestrictedMentionFlagTimeout.current);
					setCurrentPageRestrictionMentionFlagId('');
					void setPageRestrictedMentions([]);
				} else if (type === 'task') {
					clearTimeout(pageRestrictedTaskFlagTimeout.current);
					setCurrentPageRestrictionTaskFlagId('');
					void setPageRestrictedTasks([]);
				}
			};

			//When there is a flag currently open when a new mention is added, we want to reset the timer for the flag to be open, i.e. persist the flag to be open for another 8 seconds
			if (type === 'standard') {
				clearTimeout(pageRestrictedMentionFlagTimeout.current);
				pageRestrictedMentionFlagTimeout.current = setTimeout(() => {
					closeFlag();
				}, 8000);
			} else if (type === 'task') {
				clearTimeout(pageRestrictedTaskFlagTimeout.current);
				pageRestrictedTaskFlagTimeout.current = setTimeout(() => {
					closeFlag();
				}, 8000);
			}

			const sharedFlagFields = {
				description:
					type === 'standard'
						? formatMessage(mentionsMessages.mentionFlagNoPermissionsDescription)
						: formatMessage(mentionsMessages.taskMentionFlagNoPermissionsDescription),
				actionContent: formatMessage(mentionsMessages.mentionFlagShareNowButton),
				actionOnClick: async () => {
					//On clicking "share now" - Update page permissions
					closeFlag();

					updateUsersContentPermissionMutation({
						variables: {
							contentId,
							input: mentions?.map((user) => ({
								contentRole: ContentRole.EDITOR,
								principalId: user.id,
								principalType: PrincipalType.USER,
							})),
						},
					})
						.then(() => {
							sendNotificationFn(mentions, type, closeFlag);
						})
						.catch((e) => {
							setError(e);
							showCustomFlag({
								title: formatMessage(mentionsMessages.mentionFlagShareFailedTitle),
								description: formatMessage(mentionsMessages.mentionFlagShareFailedDescription),
							});
						});
				},
				handleOnClose: () => {
					closeFlag();
				},
				autoDismiss: false,
			};

			displayNoPermissionsFlag({
				displayNames,
				flagFields: sharedFlagFields,
				displayName,
				somebodyElse,
				flagId: addedMentionFlagId,
				type,
				hasAction: true,
				userPhoto: mentions[0]?.userPhoto,
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
			contentId,
			createAnalyticsEvent,
			currentPageRestrictionMentionFlagId,
			currentPageRestrictionTaskFlagId,
			displayNoPermissionsFlag,
			flags,
			formatMessage,
			setPageRestrictedMentions,
			setPageRestrictedTasks,
			showCustomFlag,
			updateUsersContentPermissionMutation,
		],
	);

	const showParentSpaceRestrictedFlag = useCallback(
		({ type, mentions }: { type: 'standard' | 'task'; mentions: MentionType[] }) => {
			let addedMentionFlagId = uuid();
			//If a flag ID is already in local state, that means the respective flag is currently open. If not, we generate a uuid to set on the flag which is about to be opened. Having a uuid on the flag allows us to update the content without closing the flag
			if (type === 'standard' && currentSpaceOrParentRestrictionMentionFlagId) {
				addedMentionFlagId = currentSpaceOrParentRestrictionMentionFlagId;
			} else if (type === 'task' && currentSpaceOrParentRestrictionTaskFlagId) {
				addedMentionFlagId = currentSpaceOrParentRestrictionTaskFlagId;
			}
			if (type === 'standard' && !currentSpaceOrParentRestrictionMentionFlagId) {
				setCurrentSpaceOrParentRestrictionMentionFlagId(addedMentionFlagId);
			}
			if (type === 'task' && !currentSpaceOrParentRestrictionTaskFlagId) {
				setCurrentSpaceOrParentRestrictionTaskFlagId(addedMentionFlagId);
			}

			const { displayNames, displayName, somebodyElse } = getDisplayNames(mentions);

			const closeFlag = () => {
				void flags.hideFlag(addedMentionFlagId);
				if (type === 'standard') {
					clearTimeout(spaceParentRestrictedMentionFlagTimeout.current);
					setCurrentSpaceOrParentRestrictionMentionFlagId('');
					void setParentSpaceRestrictedMentions([]);
				} else if (type === 'task') {
					clearTimeout(spaceParentRestrictedTaskFlagTimeout.current);
					setCurrentSpaceOrParentRestrictionTaskFlagId('');
					void setParentSpaceRestrictedTasks([]);
				}
			};

			//When there is a flag currently open when a new mention is added, we want to reset the timer for the flag to be open, i.e. persist the flag to be open for another 8 seconds
			if (type === 'standard') {
				clearTimeout(spaceParentRestrictedMentionFlagTimeout.current);
				spaceParentRestrictedMentionFlagTimeout.current = setTimeout(() => {
					closeFlag();
				}, 8000);
			} else if (type === 'task') {
				clearTimeout(spaceParentRestrictedTaskFlagTimeout.current);
				spaceParentRestrictedTaskFlagTimeout.current = setTimeout(() => {
					closeFlag();
				}, 8000);
			}

			const sharedFlagFields = {
				description:
					type === 'standard'
						? formatMessage(mentionsMessages.mentionFlagNoSpaceOrParentPermissionsDescription)
						: formatMessage(
								mentionsMessages.taskMentionFlagNoPermissionsParentSpaceRestrictionsDescription,
							),
				handleOnClose: () => {
					closeFlag();
				},
				autoDismiss: false,
			};

			displayNoPermissionsFlag({
				displayNames,
				flagFields: sharedFlagFields,
				displayName,
				somebodyElse,
				type,
				flagId: addedMentionFlagId,
				userPhoto: mentions[0]?.userPhoto,
			});
		},
		[
			currentSpaceOrParentRestrictionMentionFlagId,
			currentSpaceOrParentRestrictionTaskFlagId,
			formatMessage,
			displayNoPermissionsFlag,
			flags,
			setParentSpaceRestrictedMentions,
			setParentSpaceRestrictedTasks,
		],
	);

	// Hook for showing error flag without display name
	useEffect(() => {
		if (!isMentionsNotificationsEnabled) {
			return;
		}

		if (userInfoError || contentPermissionsError) {
			showCustomFlag({
				title: formatMessage(mentionsMessages.mentionFlagFailedTitleWithoutDisplayName),
				description: formatMessage(mentionsMessages.mentionFlagFailedDescription),
			});
		}
	}, [
		isMentionsNotificationsEnabled,
		formatMessage,
		userInfoError,
		contentPermissionsError,
		showCustomFlag,
	]);

	useEffect(() => {
		if (!isMentionsNotificationsEnabled) {
			return;
		}

		if (userInfoData?.user && !contentPermissionsLoading) {
			const { displayName, photos } = userInfoData.user;
			const userPhoto = photos[0]?.value;

			if (!isSidebarPresent) {
				if (!userHasAccessToContent) {
					showFlagForRestrictedUser({ displayName, userPhoto });
				} else {
					showFlagForUnrestrictedUser({ displayName, userPhoto });
				}
			} else if (isSidebarPresent && !userHasAccessToContent) {
				showFlagForRestrictedUser({ displayName, userPhoto });
			}
		}

		// `isSidebarPresent` is left out of dependeny array because a flag will show up anytime
		// the inline comments sidebar is dismissed / brought up after an @ mention. We want this
		// hook to run only once per mention.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		userInfoData,
		isMentionsNotificationsEnabled,
		contentPermissionsLoading,
		userHasAccessToContent,
		showFlagForRestrictedUser,
		showFlagForUnrestrictedUser,
	]);

	useEffect(() => {
		//Handling deleted mentions
		if (
			deletedMentionedUsersInfoData?.length &&
			isMentionsNotificationsEnabled &&
			fg('deleted_mentions_flags_live_pages')
		) {
			if (addedMentions.length === 1) {
				//We only show the deleted flag if there is one deleted mention in the current open flag
				const name = deletedMentionedUsersInfoData[0]?.displayName;
				showCustomFlagWithAction({
					title: formatMessage(mentionsMessages.deletedMentionFlagTitle, {
						name,
					}),
					description: formatMessage(mentionsMessages.deletedMentionFlagDescription, {
						name,
					}),
					actionContent: formatMessage(mentionsMessages.deletedMentionFlagConfirmation),
					actionOnClick: () => {
						void setDeletedMentions([]);
						const flagToDismiss = flags.state.flags[0]?.id;
						void flags.hideFlag(flagToDismiss);
					},
					handleOnClose: () => {
						void setDeletedMentions([]);
						const flagToDismiss = flags.state.flags[0]?.id;
						void flags.hideFlag(flagToDismiss);
					},
				});
				clearTimeout(addedUnrestrictedMentionFlagTimeout.current);
				void flags.hideFlag(currentAddedMentionFlagId);
				setCurrentAddedMentionFlagId('');
				void setAddedMentions([]);
			}
		}
		if (deletedMentions.length > 0) {
			//Handles updating content of current open mention flags
			const deletedMentionsLocalIds = new Set(deletedMentions.map((mention) => mention.localId));
			//Filter out deleted mentions from current open general mention flag
			const remainingUnrestrictedStandardMentions = unrestrictedMentions.filter(
				(unrestrictedMention) => !deletedMentionsLocalIds.has(unrestrictedMention.localId),
			);
			const remainingParentSpaceRestrictedStandardMentions = parentSpaceRestrictedMentions.filter(
				(parentSpaceRestrictedMention) =>
					!deletedMentionsLocalIds.has(parentSpaceRestrictedMention.localId),
			);
			const remainingPageRestrictedStandardMentions = pageRestrictedMentions.filter(
				(pageRestrictedMention) => !deletedMentionsLocalIds.has(pageRestrictedMention.localId),
			);

			//Close general mention flag if none remain
			if (remainingUnrestrictedStandardMentions?.length === 0) {
				clearTimeout(addedUnrestrictedMentionFlagTimeout.current);
				void flags.hideFlag(currentAddedMentionFlagId);
				setCurrentAddedMentionFlagId('');
			}
			if (remainingParentSpaceRestrictedStandardMentions?.length === 0) {
				clearTimeout(spaceParentRestrictedMentionFlagTimeout.current);
				void flags.hideFlag(currentSpaceOrParentRestrictionMentionFlagId);
				setCurrentSpaceOrParentRestrictionMentionFlagId('');
			}
			if (remainingPageRestrictedStandardMentions?.length === 0) {
				clearTimeout(pageRestrictedMentionFlagTimeout.current);
				void flags.hideFlag(currentPageRestrictionMentionFlagId);
				setCurrentPageRestrictionMentionFlagId('');
			}
			void setUnrestrictedMentions(remainingUnrestrictedStandardMentions);
			void setParentSpaceRestrictedMentions(remainingParentSpaceRestrictedStandardMentions);
			void setPageRestrictedMentions(remainingPageRestrictedStandardMentions);
			void setDeletedMentions([]);
		}
		if (deletedTasks.length > 0) {
			//Handles updating content of current open task flags
			const deletedTasksLocalIds = new Set(deletedTasks.map((mention) => mention.localId));
			//Filter out deleted mentions from current open task mention flag
			const remainingUnrestrictedTasks = unrestrictedTasks.filter(
				(unrestrictedTask) => !deletedTasksLocalIds.has(unrestrictedTask.localId),
			);
			const remainingParentSpaceRestrictedTasks = parentSpaceRestrictedTasks.filter(
				(parentSpaceRestrictedTask) => !deletedTasksLocalIds.has(parentSpaceRestrictedTask.localId),
			);
			const remainingPageRestrictedTasks = pageRestrictedTasks.filter(
				(pageRestrictedTask) => !deletedTasksLocalIds.has(pageRestrictedTask.localId),
			);

			//Close task mention flag if none remain
			if (remainingUnrestrictedTasks?.length === 0) {
				clearTimeout(addedUnrestrictedTaskFlagTimeout.current);
				void flags.hideFlag(currentAddedTaskFlagId);
				setCurrentAddedTaskFlagId('');
			}
			if (remainingParentSpaceRestrictedTasks?.length === 0) {
				clearTimeout(spaceParentRestrictedTaskFlagTimeout.current);
				void flags.hideFlag(currentSpaceOrParentRestrictionTaskFlagId);
				setCurrentSpaceOrParentRestrictionTaskFlagId('');
			}
			if (remainingPageRestrictedTasks?.length === 0) {
				clearTimeout(pageRestrictedTaskFlagTimeout.current);
				void flags.hideFlag(currentPageRestrictionTaskFlagId);
				setCurrentPageRestrictionTaskFlagId('');
			}
			void setUnrestrictedTasks(remainingUnrestrictedTasks);
			void setParentSpaceRestrictedTasks(remainingParentSpaceRestrictedTasks);
			void setPageRestrictedTasks(remainingPageRestrictedTasks);
			void setDeletedTasks([]);
		}
		//leaving addedMentions out of the dependency array otherwise the deleted flag will be displayed twice
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		setDeletedMentions,
		flags,
		showCustomFlagWithAction,
		deletedMentionedUsersInfoData,
		formatMessage,
		setAddedMentions,
		deletedMentions,
		setDeletedTasks,
		deletedTasks,
	]);

	useEffect(() => {
		if (!usersContentPermissionsLoading && unrestrictedMentions?.length) {
			showUnrestrictedPageAccessFlag({
				type: 'standard',
				mentions: unrestrictedMentions,
				userPhoto: unrestrictedMentions[0]?.userPhoto,
			});
		}
		// removing variables from dependency array which cause duplicate flags
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [flags, unrestrictedMentions, usersContentPermissionsLoading]);

	useEffect(() => {
		if (!usersContentPermissionsLoading && unrestrictedTasks?.length) {
			showUnrestrictedPageAccessFlag({
				type: 'task',
				mentions: unrestrictedTasks,
				userPhoto: unrestrictedTasks[0]?.userPhoto,
			});
		}
		// removing variables from dependency array which cause duplicate flags
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [flags, unrestrictedTasks, usersContentPermissionsLoading]);

	useEffect(() => {
		if (!usersContentPermissionsLoading && parentSpaceRestrictedMentions?.length) {
			showParentSpaceRestrictedFlag({ type: 'standard', mentions: parentSpaceRestrictedMentions });
		}
		// removing variables from dependency array which cause duplicate flags
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [flags, parentSpaceRestrictedMentions, usersContentPermissionsLoading]);

	useEffect(() => {
		if (!usersContentPermissionsLoading && parentSpaceRestrictedTasks?.length) {
			showParentSpaceRestrictedFlag({ type: 'task', mentions: parentSpaceRestrictedTasks });
		}
		// removing variables from dependency array which cause duplicate flags
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [flags, parentSpaceRestrictedTasks, usersContentPermissionsLoading]);

	useEffect(() => {
		if (!usersContentPermissionsLoading && pageRestrictedMentions?.length) {
			showPageRestrictionsFlag({ type: 'standard', mentions: pageRestrictedMentions });
		}
		// removing variables from dependency array which cause duplicate flags
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [flags, pageRestrictedMentions, usersContentPermissionsLoading]);

	useEffect(() => {
		if (!usersContentPermissionsLoading && pageRestrictedTasks?.length) {
			showPageRestrictionsFlag({ type: 'task', mentions: pageRestrictedTasks });
		}
		// removing variables from dependency array which cause duplicate flags
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [flags, pageRestrictedTasks, usersContentPermissionsLoading]);

	useEffect(() => {
		if (mentionsAdded.currentUser.length > 0) {
			const { mentionsWithNotificationAction } = getNotificationPayload(
				NotificationAction.DONT_NOTIFY,
				mentionsAdded.currentUser,
				'standard',
			);
			void sendCancelMentionNotification({
				mentions: mentionsWithNotificationAction,
			});
			void setMentionsAdded({ currentUser: [] });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [mentionsAdded.currentUser]);

	useEffect(() => {
		if (tasksAdded.currentUser.length > 0) {
			const { tasksWithNotificationAction } = getNotificationPayload(
				NotificationAction.DONT_NOTIFY,
				tasksAdded.currentUser,
				'task',
			);
			void cancelInlineTaskNotifications({
				taskItems: tasksWithNotificationAction,
			});
			void setTasksAdded({ currentUser: [] });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tasksAdded.currentUser]);

	const eventHandlersDisabled =
		!isMentionsNotificationsEnabled ||
		(isMentionsNotificationsEnabled && fg('confluence_updated_mentions_livepages'));
	const handleOnMentionClickEvent = (event: UIAnalyticsEvent) => {
		if (eventHandlersDisabled) {
			return;
		}

		const { actionSubject, action, attributes } = event.payload;
		if (actionSubject === 'mentionTypeahead' && (action === 'clicked' || action === 'pressed')) {
			if (attributes.userType === 'TEAM') {
				// TODO: support group mentions when batching is implemented
				return;
			}

			// Reset mentionedUserId for the case where the 2nd mentioned user is the same as the 1st
			resetState();

			const mentionLocalId = attributes.localId;
			setMentionLocalId(mentionLocalId);

			const accountId = attributes.userId;
			if (attributes.taskItemId) {
				setTaskItem({
					operation: Operation.ASSIGNED,
					recipientAccountId: accountId,
					// `taskId` in a draft is initially set as a uuid (e.g. 123e456-789b-12d3-a456)
					// and is converted to a numeric digit after publish (e.g. 567).
					// The BE is expecting taskId to be a long, which cannot contain characters like a hyphen
					// and thus `createInlineTaskNotificationMutation` will fail if we pass in a uuid (an invalid type).
					// To circumvent this limitation, send a dummy `taskId` of 0; this means the email notification
					// will not contain page context and will simply state that the user has been assigned a task on a page.
					taskId: '0',
				});
			}

			setMentionedUserId(accountId);
		}
	};

	const handleOnMentionPasteEvent = (event: UIAnalyticsEvent) => {
		if (eventHandlersDisabled) {
			return;
		}

		const { actionSubject, action, attributes } = event.payload;
		if (actionSubject === 'document' && action === 'pasted') {
			resetState();

			// `attributes.mentionIds` will be an empty array when pasting any text that
			// isn't a mention, so we can safely access it here
			const mentionedUserIds = attributes.mentionIds;
			if (!mentionedUserIds.length || mentionedUserIds.length > 1) {
				// TODO: support multiple pasted mentioned IDs

				return;
			}

			const accountId = mentionedUserIds[0];
			const localId = attributes.mentionLocalIds[0];
			setMentionedUserId(accountId);
			setMentionLocalId(localId);
		}
	};

	return (
		<AnalyticsListener onEvent={handleOnMentionClickEvent} channel={FabricChannel.elements}>
			<AnalyticsListener onEvent={handleOnMentionPasteEvent} channel={FabricChannel.editor}>
				{error && <ErrorDisplay error={error} />}
				{usersContentPermissionsError && <ErrorDisplay error={usersContentPermissionsError} />}
				{deletedMentionedUsersInfoError && <ErrorDisplay error={deletedMentionedUsersInfoError} />}
				{createTaskNotificationError && <ErrorDisplay error={createTaskNotificationError} />}
				{contentMentionNotificationError && (
					<ErrorDisplay error={contentMentionNotificationError} />
				)}
				{children}
			</AnalyticsListener>
		</AnalyticsListener>
	);
};
